import { motion, AnimatePresence } from 'framer-motion'
import { HiX, HiMinus, HiPlus, HiTrash, HiUser } from 'react-icons/hi'
import { Link } from 'react-router-dom'
import { useCart } from '../../context/CartContext'
import { useAuth } from '../../context/AuthContext'
import Button from '../ui/Button'

function CartItem({ item }) {
  const { removeItem, updateItem, formatPrice, currencySymbol } = useCart()
  const image = item.images?.[0]?.src

  return (
    <div className="flex gap-4 py-4 border-b border-light-200 last:border-0">
      {image && (
        <img
          src={image}
          alt={item.name}
          className="w-20 h-20 object-cover rounded-xl shrink-0 bg-light-100"
        />
      )}
      <div className="flex-1 min-w-0">
        <p className="font-body font-semibold text-sm text-ink leading-snug line-clamp-2">{item.name}</p>
        <p className="font-body text-sm text-primary font-bold mt-1">
          {currencySymbol} {formatPrice(item.totals.line_total)}
        </p>

        <div className="flex items-center gap-2 mt-2">
          <button
            onClick={() => updateItem(item.key, item.quantity - 1)}
            className="w-7 h-7 rounded-full bg-light-200 flex items-center justify-center hover:bg-light-300 transition-colors"
          >
            <HiMinus size={12} />
          </button>
          <span className="font-body text-sm w-5 text-center tabular-nums">{item.quantity}</span>
          <button
            onClick={() => updateItem(item.key, item.quantity + 1)}
            className="w-7 h-7 rounded-full bg-light-200 flex items-center justify-center hover:bg-light-300 transition-colors"
          >
            <HiPlus size={12} />
          </button>
          <button
            onClick={() => removeItem(item.key)}
            className="w-7 h-7 rounded-full bg-red-50 text-red-400 flex items-center justify-center hover:bg-red-100 transition-colors ml-auto"
          >
            <HiTrash size={13} />
          </button>
        </div>
      </div>
    </div>
  )
}

export default function CartDrawer() {
  const { cart, isOpen, setIsOpen, formatPrice, currencySymbol } = useCart()
  const { isLoggedIn, openAuthModal } = useAuth()
  const items = cart?.items ?? []

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            key="cart-backdrop"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-ink/40 backdrop-blur-sm z-[55]"
          />

          <motion.div
            key="cart-drawer"
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', stiffness: 300, damping: 32 }}
            className="fixed top-0 right-0 h-full w-full max-w-sm bg-white shadow-2xl z-[56] flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-light-200">
              <h2 className="font-display text-xl text-ink">
                Your Cart
                {items.length > 0 && (
                  <span className="ml-2 text-sm font-body font-normal text-ink-muted">
                    ({items.length} {items.length === 1 ? 'item' : 'items'})
                  </span>
                )}
              </h2>
              <button
                onClick={() => setIsOpen(false)}
                className="w-9 h-9 rounded-full bg-light-200 flex items-center justify-center hover:bg-light-300 transition-colors"
              >
                <HiX size={18} />
              </button>
            </div>

            {/* Items */}
            <div className="flex-1 overflow-y-auto px-6">
              {items.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full gap-4 text-center">
                  <span className="text-5xl">🛒</span>
                  <p className="font-body text-ink-muted">Your cart is empty.</p>
                  <Button variant="secondary" size="sm" to="/shop" onClick={() => setIsOpen(false)}>
                    Browse Products
                  </Button>
                </div>
              ) : (
                items.map((item) => <CartItem key={item.key} item={item} />)
              )}
            </div>

            {/* Footer */}
            {items.length > 0 && (
              <div className="px-6 py-5 border-t border-light-200 space-y-4">
                {/* Auth prompt for guests */}
                {!isLoggedIn && (
                  <div className="p-3 rounded-2xl bg-primary/5 border border-primary/15">
                    <div className="flex items-center gap-2 mb-2">
                      <HiUser size={14} className="text-primary shrink-0" />
                      <p className="font-body text-xs text-ink/70 font-semibold">
                        Sign in to save your order history
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => openAuthModal('login')}
                        className="flex-1 py-2 rounded-full border border-primary text-primary font-body text-xs font-semibold hover:bg-primary hover:text-white transition-colors duration-200"
                      >
                        Sign In
                      </button>
                      <button
                        onClick={() => openAuthModal('register')}
                        className="flex-1 py-2 rounded-full bg-primary text-white font-body text-xs font-semibold hover:bg-primary-dark transition-colors duration-200"
                      >
                        Register
                      </button>
                    </div>
                  </div>
                )}

                <div className="flex justify-between items-center">
                  <span className="font-body text-ink-muted">Total</span>
                  <span className="font-display text-xl text-ink">
                    {currencySymbol} {formatPrice(cart?.totals?.total_price)}
                  </span>
                </div>
                <Link to="/checkout" onClick={() => setIsOpen(false)}>
                  <Button variant="primary" className="w-full">Proceed to Checkout</Button>
                </Link>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
